import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { Toast } from '@/components/common';
import {
  fetchGetApplicationDetail,
  fetchGetApplicationReviewRecords,
  fetchGetApplicationReviewResult,
  fetchSupplementApplication,
  fetchSubmitApplication,
  fetchCopyApplication,
} from '@/service/api';
import { useChannelStore } from '@/stores';
import { useDict } from '@/hooks/useDict';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';

function isImageFile(asset?: Api.Application.AssetResponse | null, displayName?: string): boolean {
  if (!asset && !displayName) return false;
  const mime = (asset?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const filename = (asset?.file_name || displayName || '').toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(filename);
}

function isVideoFile(asset?: Api.Application.AssetResponse | null, displayName?: string): boolean {
  if (!asset && !displayName) return false;
  const mime = (asset?.mime_type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const filename = (asset?.file_name || displayName || '').toLowerCase();
  return /\.(mp4|webm|mov|m4v|mkv|avi)$/i.test(filename);
}

interface StatusConfigItem {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  icon: string;
}

const statusConfig: Record<string, StatusConfigItem> = {
  DRAFT: {
    label: '草稿',
    color: 'bg-gray-100 text-gray-700 border border-gray-200/60',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-700',
    icon: 'ri-draft-line',
  },
  PENDING_REVIEW: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200/60',
    textColor: 'text-amber-900',
    icon: 'ri-time-line',
  },
  PENDING: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200/60',
    textColor: 'text-amber-900',
    icon: 'ri-time-line',
  },
  UNDER_REVIEW: {
    label: '审核中',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200/60',
    textColor: 'text-amber-900',
    icon: 'ri-loader-4-line',
  },
  IN_REVIEW: {
    label: '审核中',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200/60',
    textColor: 'text-amber-900',
    icon: 'ri-loader-4-line',
  },
  SUBMITTED: {
    label: '已提交',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    bgColor: 'bg-amber-50/80',
    borderColor: 'border-amber-200/60',
    textColor: 'text-amber-900',
    icon: 'ri-send-plane-line',
  },
  SUPPLEMENT_REQUIRED: {
    label: '需补充材料',
    color: 'bg-orange-100 text-orange-800 border border-orange-200/60',
    bgColor: 'bg-orange-50/80',
    borderColor: 'border-orange-200/60',
    textColor: 'text-orange-900',
    icon: 'ri-add-circle-line',
  },
  NEED_SUPPLEMENT: {
    label: '需补充材料',
    color: 'bg-orange-100 text-orange-800 border border-orange-200/60',
    bgColor: 'bg-orange-50/80',
    borderColor: 'border-orange-200/60',
    textColor: 'text-orange-900',
    icon: 'ri-add-circle-line',
  },
  REJECTED: {
    label: '已驳回',
    color: 'bg-red-50 text-red-600 border border-red-200/60',
    bgColor: 'bg-red-50/80',
    borderColor: 'border-red-200/60',
    textColor: 'text-red-900',
    icon: 'ri-close-circle-line',
  },
  REFUSED: {
    label: '已驳回',
    color: 'bg-red-50 text-red-600 border border-red-200/60',
    bgColor: 'bg-red-50/80',
    borderColor: 'border-red-200/60',
    textColor: 'text-red-900',
    icon: 'ri-close-circle-line',
  },
  APPROVED: {
    label: '已通过',
    color: 'bg-emerald-100 text-emerald-800 border border-emerald-200/60',
    bgColor: 'bg-emerald-50/80',
    borderColor: 'border-emerald-200/60',
    textColor: 'text-emerald-900',
    icon: 'ri-checkbox-circle-line',
  },
  PASSED: {
    label: '已通过',
    color: 'bg-emerald-100 text-emerald-800 border border-emerald-200/60',
    bgColor: 'bg-emerald-50/80',
    borderColor: 'border-emerald-200/60',
    textColor: 'text-emerald-900',
    icon: 'ri-checkbox-circle-line',
  },
  CONVERTED_TO_PROJECT: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    bgColor: 'bg-blue-50/80',
    borderColor: 'border-blue-200/60',
    textColor: 'text-blue-900',
    icon: 'ri-folder-shared-line',
  },
  SIGNED: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    bgColor: 'bg-blue-50/80',
    borderColor: 'border-blue-200/60',
    textColor: 'text-blue-900',
    icon: 'ri-folder-shared-line',
  },
  COMPLETED: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    bgColor: 'bg-blue-50/80',
    borderColor: 'border-blue-200/60',
    textColor: 'text-blue-900',
    icon: 'ri-folder-shared-line',
  },
};

function formatDate(dateStr?: string) {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${min}`;
  } catch {
    return dateStr;
  }
}

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 接入通用字典 Hook
  const { getLabel: getContentTypeLabel } = useDict('CONTENT_TYPE');
  const { getLabel: getGenreLabel } = useDict('GENRE');
  const { getLabel: getOrientationLabel } = useDict('CONTENT_ORIENTATION');
  const { getLabel: getLanguageLabel } = useDict('LANGUAGE');
  const { getLabel: getAudienceLabel } = useDict('AUDIENCE');

  const formatContentType = (code?: string) => {
    if (!code) return '未填';
    const presetMap: Record<string, string> = {
      SHORT_DRAMA: '短剧',
      MICRO_MOVIE: '微电影',
      ANIMATION: '动漫',
      OTHER: '其他',
    };
    return getContentTypeLabel(code, presetMap[code] || code);
  };

  const formatOrientation = (code?: string) => {
    if (!code) return '-';
    const presetMap: Record<string, string> = {
      VERTICAL: '竖屏',
      HORIZONTAL: '横屏',
    };
    return getOrientationLabel(code, presetMap[code] || code);
  };

  const formatLanguage = (code?: string) => {
    if (!code) return '-';
    const presetMap: Record<string, string> = {
      'zh-CN': '中文简体',
      'en-US': '英语',
      'ja-JP': '日语',
      'ko-KR': '韩语',
    };
    return getLanguageLabel(code, presetMap[code] || code);
  };

  const formatAudience = (code?: string) => {
    if (!code) return '-';
    const presetMap: Record<string, string> = {
      GENERAL: '全人群/通用',
      TEEN: '青少年',
      ADULT: '成人群体',
      KIDS: '少儿/儿童',
    };
    return getAudienceLabel(code, presetMap[code] || code);
  };

  const formatGenre = (code: string) => {
    const presetMap: Record<string, string> = {
      SWEET_LOVE: '甜宠',
      CEO: '霸总',
      WARRIOR: '战神',
      URBAN: '都市',
      SUSPENSE: '悬疑',
      FANTASY: '奇幻',
      COSTUME: '古装',
      REVENGE: '复仇',
      XIANXIA: '修仙',
      SCI_FI: '科幻',
    };
    return getGenreLabel(code, presetMap[code] || code);
  };

  const formatAttachmentCategory = (category?: string, isSupplement?: boolean) => {
    if (!category) {
      return isSupplement ? '补充资料' : '资质材料';
    }
    const categoryMap: Record<string, string> = {
      APPLICATION_COVER: '封面',
      APPLICATION_TRAILER: '宣传片',
      APPLICATION_POSTER: '海报',
      COPYRIGHT_CHAIN: '版权授权链',
      COPYRIGHT_CORE: '核心版权证明',
      COPYRIGHT_AUXILIARY: '辅助版权资料',
      QUALIFICATION: '资质能力证明',
      EPISODE: '剧集素材',
      OTHER: '其他补充资料',
    };
    return categoryMap[category] || category || (isSupplement ? '补充资料' : '资质材料');
  };

  const formatSubjectType = (type?: string) => {
    if (!type) return '个人';
    const t = type.toUpperCase();
    if (t === 'PERSONAL' || t === 'INDIVIDUAL') return '个人';
    if (t === 'ENTERPRISE' || t === 'COMPANY') return '企业';
    return type;
  };
  const initialTab = (searchParams.get('tab') as 'overview' | 'review' | 'episodes' | 'copyright') || 'overview';

  const [activeTab, setActiveTab] = useState<'overview' | 'review' | 'episodes' | 'copyright'>(initialTab);
  const [application, setApplication] = useState<Api.Application.ApplicationResponse | null>(null);
  const [reviewRecords, setReviewRecords] = useState<Api.Application.ApplicantApplicationReviewTimelineResponse | null>(null);
  const [reviewResult, setReviewResult] = useState<Api.Application.ApplicationResponse | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // 补充资料 Modal State
  const [isSupplementOpen, setIsSupplementOpen] = useState(false);
  const [supplementCategory, setSupplementCategory] = useState<string>('OTHER');
  const [supplementDisplayName, setSupplementDisplayName] = useState<string>('');
  const [supplementAssetId, setSupplementAssetId] = useState<string>('');
  const [supplementSubmitting, setSupplementSubmitting] = useState(false);

  // 全屏媒体 (图片/视频) 预览 Modal State
  const [previewMedia, setPreviewMedia] = useState<{
    open: boolean;
    title?: string;
    mediaUrl?: string | null;
    isVideo?: boolean;
  }>({ open: false });

  const { fetchChannels, getChannelNameById } = useChannelStore();

  const showToast = (text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadDetailData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      // 并行请求 4 个接口中的核心读取接口
      const [appRes, recordsRes, resultRes] = await Promise.allSettled([
        fetchGetApplicationDetail(id),
        fetchGetApplicationReviewRecords(id),
        fetchGetApplicationReviewResult(id),
      ]);

      if (appRes.status === 'fulfilled') {
        setApplication(appRes.value);
      } else {
        throw new Error(appRes.reason?.message || '无法获取发行申请详情');
      }

      if (recordsRes.status === 'fulfilled') {
        setReviewRecords(recordsRes.value);
      }

      if (resultRes.status === 'fulfilled') {
        setReviewResult(resultRes.value);
      }
    } catch (err: any) {
      setError(err?.message || '加载发行申请详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchChannels();
    loadDetailData();
  }, [fetchChannels, loadDetailData]);

  const handleTabChange = (tab: 'overview' | 'review' | 'episodes' | 'copyright') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // 提交草稿申请
  const handleSubmitApp = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await fetchSubmitApplication(id);
      showToast('发行申请提交成功，请等待平台审核', 'success');
      loadDetailData();
    } catch (err: any) {
      showToast(err?.message || '提交发行申请失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // 复制为新申请
  const handleCopyApp = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      const res = await fetchCopyApplication(id);
      showToast('已成功复制为新申请草稿', 'success');
      if (res?.id) {
        navigate(`/apply?id=${res.id}`);
      }
    } catch (err: any) {
      showToast(err?.message || '复制失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // 提交补充资料表单 (/applications/{id}/supplement)
  const handleSupplementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!supplementDisplayName.trim()) {
      showToast('请输入补充资料文件名称', 'error');
      return;
    }
    setSupplementSubmitting(true);
    try {
      await fetchSupplementApplication(id, {
        application_attachments: [
          {
            category: supplementCategory,
            display_name: supplementDisplayName.trim(),
            upload_asset_id: supplementAssetId.trim() || undefined,
            visibility: 'FRONTEND_VISIBLE',
          },
        ],
      });
      showToast('补充资料已提交成功', 'success');
      setIsSupplementOpen(false);
      setSupplementDisplayName('');
      setSupplementAssetId('');
      loadDetailData();
    } catch (err: any) {
      showToast(err?.message || '提交补充资料失败', 'error');
    } finally {
      setSupplementSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Navbar />
        <div className="pt-28 sm:pt-32 pb-20 w-full max-w-[1400px] mx-auto px-6 md:px-12 flex-1 flex flex-col justify-center items-center">
          <div className="flex flex-col items-center gap-3 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="text-sm">正在加载发行申请详情...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Navbar />
        <div className="pt-28 sm:pt-32 pb-20 w-full max-w-[1400px] mx-auto px-6 md:px-12 flex-1 flex flex-col justify-center items-center">
          <div className="bg-white p-8 rounded-2xl border border-red-100 shadow-sm text-center max-w-md w-full">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="ri-error-warning-line"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-900 mb-2">获取申请详情失败</h3>
            <p className="text-sm text-foreground-500 mb-6">{error || '未能找到对应的发行申请条目'}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => loadDetailData()}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-foreground-700 text-sm font-medium transition-all"
              >
                重试
              </button>
              <Link
                to="/applications"
                className="px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-foreground-950 text-sm font-semibold transition-all"
              >
                返回申请列表
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rawStatus = (application.status || '').toUpperCase();
  const statusInfo = statusConfig[rawStatus] || statusConfig[application.status || ''] || {
    label: application.status || '未知状态',
    color: 'bg-gray-100 text-gray-700 border border-gray-200',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-700',
    icon: 'ri-question-line',
  };

  const isNeedSupplement = ['SUPPLEMENT_REQUIRED', 'NEED_SUPPLEMENT'].includes(rawStatus);
  const isRejected = ['REJECTED', 'REFUSED'].includes(rawStatus);
  const isApproved = ['APPROVED', 'PASSED'].includes(rawStatus);
  const isConverted = ['CONVERTED_TO_PROJECT', 'SIGNED', 'COMPLETED'].includes(rawStatus);
  const isPending = ['PENDING', 'PENDING_REVIEW', 'UNDER_REVIEW', 'IN_REVIEW', 'SUBMITTED', 'REVIEWING'].includes(rawStatus);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50/70">
      <Navbar />

      {/* Toast 提示 */}
      {toast && (
        <Toast
          message={toast.text}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pt-24 sm:pt-28 pb-16 flex-1">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 space-y-6">
          {/* 面包屑导航 */}
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/applications" className="hover:text-foreground-700 transition-colors">发行申请</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium truncate max-w-[200px] sm:max-w-none">
              {application.title || application.application_no || '申请详情'}
            </span>
          </nav>

          {/* Header 卡片 */}
          <div className="bg-white rounded-2xl p-5 sm:p-7 border border-gray-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4 min-w-0">
              {/* 封面快照 */}
              {application.cover?.access_url ? (
                <img
                  src={application.cover.access_url}
                  alt={application.title}
                  className="w-16 h-22 sm:w-20 sm:h-28 rounded-xl object-cover border border-gray-200 shrink-0 shadow-2xs"
                />
              ) : (
                <div className="w-16 h-22 sm:w-20 sm:h-28 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center text-foreground-400 shrink-0 border border-gray-200/80">
                  <i className="ri-film-line text-2xl"></i>
                  <span className="text-2xs mt-1">无封面</span>
                </div>
              )}

              <div className="space-y-2 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="font-mono text-xs text-foreground-400 font-medium">
                    {application.application_no || application.id}
                  </span>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${statusInfo.color}`}>
                    <i className={`${statusInfo.icon} text-sm`}></i>
                    {statusInfo.label}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-foreground-950 truncate">
                  {application.title || '未命名短剧申请'}
                </h1>
                {application.subtitle && (
                  <p className="text-xs sm:text-sm text-foreground-500 truncate">{application.subtitle}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 pt-1">
                  {application.submitted_at && <span>提交时间: {formatDate(application.submitted_at)}</span>}
                  {application.created_at && <span>创建时间: {formatDate(application.created_at)}</span>}
                  {application.total_episode_count && <span>共 {application.total_episode_count} 集</span>}
                </div>
              </div>
            </div>

            {/* 操作按钮组 */}
            <div className="flex flex-wrap items-center gap-3 shrink-0 pt-4 md:pt-0 border-t md:border-t-0 border-gray-100">
              {rawStatus === 'DRAFT' && (
                <>
                  <Link
                    to={`/apply?id=${application.id}`}
                    className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-foreground-700 text-sm font-medium transition-all"
                  >
                    继续编辑
                  </Link>
                  <button
                    type="button"
                    onClick={handleSubmitApp}
                    disabled={actionLoading}
                    className="px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-foreground-950 text-sm font-semibold transition-all shadow-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {actionLoading && <i className="ri-loader-4-line animate-spin"></i>}
                    <span>提交审核</span>
                  </button>
                </>
              )}

              {isNeedSupplement && (
                <button
                  type="button"
                  onClick={() => setIsSupplementOpen(true)}
                  className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold transition-all shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-add-line text-base"></i>
                  <span>补充材料资料</span>
                </button>
              )}

              {isRejected && (
                <button
                  type="button"
                  onClick={handleCopyApp}
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-foreground-950 text-sm font-semibold transition-all shadow-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {actionLoading && <i className="ri-loader-4-line animate-spin"></i>}
                  <i className="ri-file-copy-line"></i>
                  <span>复制为新申请</span>
                </button>
              )}

              {isConverted && (
                <Link
                  to={application.project_id ? `/projects/${application.project_id}` : '/projects'}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-all shadow-xs inline-flex items-center gap-1.5"
                >
                  <i className="ri-external-link-line"></i>
                  <span>查看关联项目</span>
                </Link>
              )}

              <Link
                to="/applications"
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-foreground-700 text-sm font-medium transition-all"
              >
                返回列表
              </Link>
            </div>
          </div>

          {/* 状态高亮提示 Banner */}
          {isNeedSupplement && (
            <div className="p-4 sm:p-5 rounded-2xl border border-orange-200 bg-orange-50/90 text-orange-950 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 text-xl mt-0.5">
                  <i className="ri-alert-line"></i>
                </div>
                <div>
                  <h4 className="text-base font-bold text-orange-900">需要补充材料 / 审核反馈说明</h4>
                  <p className="text-xs sm:text-sm text-orange-800 mt-1">
                    {reviewResult?.audit_remark || application.audit_remark || '审核人员提出了补充资料要求，请根据提示上传或更新相关资料后再次提交。'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSupplementOpen(true)}
                className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-xs sm:text-sm font-semibold transition-all shadow-xs whitespace-nowrap shrink-0 self-start sm:self-auto"
              >
                立即补充材料
              </button>
            </div>
          )}

          {isRejected && (
            <div className="p-4 sm:p-5 rounded-2xl border border-red-200 bg-red-50/90 text-red-950 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0 text-xl mt-0.5">
                <i className="ri-close-circle-line"></i>
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-red-900">发行申请未通过审核</h4>
                <p className="text-xs sm:text-sm text-red-800 mt-1">
                  驳回原因：{reviewResult?.audit_remark || application.audit_remark || '审核未通过，请查阅“审核记录与意见”了解详情，或将其复制为新申请重新填写提交。'}
                </p>
              </div>
            </div>
          )}

          {isApproved && (
            <div className="p-4 sm:p-5 rounded-2xl border border-emerald-200 bg-emerald-50/90 text-emerald-950 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 text-xl mt-0.5">
                <i className="ri-checkbox-circle-line"></i>
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-emerald-900">发行申请审核已通过</h4>
                <p className="text-xs sm:text-sm text-emerald-800 mt-1">
                  审核通过时间: {formatDate(application.approved_at || application.created_at)}。稍后将由平台转换为发行项目进行后续运营发售。
                </p>
              </div>
            </div>
          )}

          {isPending && (
            <div className="p-4 sm:p-5 rounded-2xl border border-amber-200 bg-amber-50/90 text-amber-950 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0 text-xl mt-0.5">
                <i className="ri-time-line"></i>
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-amber-900">申请审核进行中</h4>
                <p className="text-xs sm:text-sm text-amber-800 mt-1">
                  工作人员正在查阅审核您提交的剧集基础信息、素材画质及版权资质证明，预计 3-5 个工作日内给出审片答复。
                </p>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="flex items-center gap-2 border-b border-gray-200/80 overflow-x-auto pb-0.5 scrollbar-none">
            <button
              onClick={() => handleTabChange('overview')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${activeTab === 'overview'
                ? 'border-primary-500 text-foreground-950'
                : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-information-line mr-1.5"></i>
              申请概览与基本信息
            </button>
            <button
              onClick={() => handleTabChange('review')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer relative ${activeTab === 'review'
                ? 'border-primary-500 text-foreground-950'
                : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-history-line mr-1.5"></i>
              审核记录与意见
              {(isNeedSupplement || isRejected) && (
                <span className="w-2 h-2 rounded-full bg-rose-500 inline-block ml-1.5 -translate-y-1"></span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('episodes')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${activeTab === 'episodes'
                ? 'border-primary-500 text-foreground-950'
                : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-movie-line mr-1.5"></i>
              剧集与视频素材 ({application.episodes?.length || 0})
            </button>
            <button
              onClick={() => handleTabChange('copyright')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${activeTab === 'copyright'
                ? 'border-primary-500 text-foreground-950'
                : 'border-transparent text-foreground-500 hover:text-foreground-800'
                }`}
            >
              <i className="ri-shield-check-line mr-1.5"></i>
              版权链与资质附件 ({application.attachments?.length || 0})
            </button>
          </div>

          {/* Tab 1: Overview */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left 2 Cols: Detailed Form Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-5">
                  <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-primary-500"></i>
                    基础元数据
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">剧集名称</span>
                      <span className="font-semibold text-foreground-900">{application.title || '-'}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">副标题</span>
                      <span className="font-medium text-foreground-800">{application.subtitle || '-'}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">内容类型</span>
                      <span className="font-medium text-foreground-800">{formatContentType(application.content_type_code)}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">画面方向</span>
                      <span className="font-medium text-foreground-800">{formatOrientation(application.orientation)}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">源语言</span>
                      <span className="font-medium text-foreground-800">{formatLanguage(application.source_language_code)}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">目标受众</span>
                      <span className="font-medium text-foreground-800">{formatAudience(application.audience_code)}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">含真人演员</span>
                      <span className="font-medium text-foreground-800">{application.has_live_actor ? '是' : '否'}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400 block text-xs mb-1">AI 生成内容</span>
                      <span className="font-medium text-foreground-800">{application.is_ai_content ? '是' : '否'}</span>
                    </div>
                  </div>

                  {application.description && (
                    <div className="pt-2 border-t border-gray-100">
                      <span className="text-foreground-400 block text-xs mb-1.5">故事梗概 / 简介</span>
                      <p className="text-sm text-foreground-700 leading-relaxed bg-gray-50/70 p-4 rounded-xl border border-gray-100">
                        {application.description}
                      </p>
                    </div>
                  )}

                  {application.genre_codes && application.genre_codes.length > 0 && (
                    <div className="pt-2">
                      <span className="text-foreground-400 block text-xs mb-2">题材标签</span>
                      <div className="flex flex-wrap gap-2">
                        {application.genre_codes.map((g) => (
                          <span key={g} className="px-3 py-1 rounded-lg bg-gray-100 text-foreground-700 text-xs font-medium">
                            {formatGenre(g)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 演职人员 */}
                {application.cast_or_role_info_json && application.cast_or_role_info_json.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-4">
                    <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center gap-2">
                      <i className="ri-team-line text-primary-500"></i>
                      演职人员表
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {application.cast_or_role_info_json.map((member, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm shrink-0">
                            {(member.name || member.role || '演').slice(0, 1)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground-900 truncate">{member.name || member.role_name || '-'}</p>
                            <p className="text-xs text-foreground-500 truncate">{member.role || member.character || '参演人员'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Col: Intent Channels & Contracts */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-4">
                  <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center gap-2">
                    <i className="ri-broadcast-line text-primary-500"></i>
                    意向发行渠道
                  </h3>
                  {application.channel_intents && application.channel_intents.length > 0 ? (
                    <div className="space-y-2">
                      {application.channel_intents.map((ch, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground-800">
                            {getChannelNameById(ch.channel_id || '', ch.channel_id || '未知渠道')}
                          </span>
                          <span className="text-2xs px-2 py-0.5 rounded-md bg-white border border-gray-200 text-foreground-500">
                            已选择
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-foreground-400 py-2">未指定特定意向渠道</p>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-4">
                  <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center gap-2">
                    <i className="ri-file-text-line text-primary-500"></i>
                    主体与合规声明
                  </h3>
                  <div className="space-y-3 text-xs text-foreground-600">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span>主体类型:</span>
                      <span className="font-semibold text-foreground-800">{formatSubjectType(application.applicant_subject_type)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span>合同签署名称:</span>
                      <span className="font-semibold text-foreground-800">{application.contract_subject_name_snapshot || '-'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span>申请合规声明:</span>
                      <span className={application.application_declaration_confirmed ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                        {application.application_declaration_confirmed ? '已确认' : '未确认'}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span>运营授权确认:</span>
                      <span className={application.operation_authorization_confirmed ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                        {application.operation_authorization_confirmed ? '已确认' : '未确认'}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>隐私协议确认:</span>
                      <span className={application.privacy_confirmed ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                        {application.privacy_confirmed ? '已确认' : '未确认'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Review Records & Results */}
          {activeTab === 'review' && (
            <div className="space-y-6">
              {/* Review Result Box */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-4">
                <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-award-line text-primary-500"></i>
                    最终审核结论与反馈
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </h3>
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-2">
                  <span className="text-xs font-semibold text-foreground-500 block">平台审核综合评语 / 备注：</span>
                  <p className="text-sm text-foreground-800 leading-relaxed font-medium">
                    {reviewResult?.audit_remark || application.audit_remark || '暂无全局审核备注，请结合具体审核记录节点查看。'}
                  </p>
                </div>
              </div>

              {/* Review Timeline / Tree */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-6">
                <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center gap-2">
                  <i className="ri-time-line text-primary-500"></i>
                  审核节点树与明细记录
                </h3>

                {reviewRecords?.review_tree ? (
                  <div className="space-y-6">
                    {/* Review Tree Recursive Rendering */}
                    <div className="relative pl-6 border-l-2 border-primary-200 space-y-6">
                      {reviewRecords.review_tree.review_type_groups && reviewRecords.review_tree.review_type_groups.length > 0 ? (
                        reviewRecords.review_tree.review_type_groups.map((group, gIdx) => (
                          <div key={gIdx} className="relative">
                            <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-primary-500 ring-4 ring-primary-100"></div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-gray-200/70 space-y-3">
                              <h4 className="text-sm font-bold text-foreground-900 flex items-center justify-between">
                                <span>审核维度: {group.review_type || '常规审片'}</span>
                              </h4>
                              {group.records && group.records.length > 0 ? (
                                <div className="space-y-2">
                                  {group.records.map((rec, rIdx) => (
                                    <div key={rIdx} className="bg-white p-3 rounded-lg border border-gray-200/60 text-xs space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="font-semibold text-foreground-800">
                                          结论: <span className={rec.result === 'APPROVED' ? 'text-emerald-600' : rec.result === 'REJECTED' ? 'text-red-600' : 'text-orange-600'}>{rec.result || '校验完成'}</span>
                                        </span>
                                        <span className="text-foreground-400">{formatDate(rec.reviewed_at || rec.created_at)}</span>
                                      </div>
                                      {rec.review_remark && (
                                        <p className="text-foreground-600 mt-1">备注说明: {rec.review_remark}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-2xs text-foreground-400">暂无具体明细行</p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="relative">
                          <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-gray-300"></div>
                          <div className="bg-gray-50 p-4 rounded-xl text-xs text-foreground-500">
                            审核时间线树已就绪，等待详细评审组数据挂载。
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-foreground-400 text-sm">
                    <i className="ri-history-line text-2xl block mb-2 opacity-50"></i>
                    暂未检索到更多审核记录节点
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab 3: Episodes */}
          {activeTab === 'episodes' && (
            <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-4">
              <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-movie-line text-primary-500"></i>
                  剧集文件列表
                </div>
                <span className="text-xs text-foreground-500">共 {application.episodes?.length || 0} 集</span>
              </h3>

              {application.episodes && application.episodes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {application.episodes.map((ep, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-gray-50 border border-gray-200/70 hover:border-primary-300 transition-all space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-foreground-900">
                          第 {ep.episode_no || idx + 1} 集: {ep.episode_title || '单集文件'}
                        </span>
                        <span className="text-2xs px-2 py-0.5 rounded-md bg-white border border-gray-200 text-foreground-600">
                          {ep.current_file?.file_size ? `${(ep.current_file.file_size / (1024 * 1024)).toFixed(1)} MB` : '云素材'}
                        </span>
                      </div>
                      {ep.current_file?.access_url && (
                        <div className="pt-2 relative group/epvid">
                          <video
                            src={ep.current_file.access_url}
                            controls
                            className="w-full h-44 rounded-lg bg-black object-contain border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setPreviewMedia({
                                open: true,
                                title: `第 ${ep.episode_no || idx + 1} 集: ${ep.episode_title || '单集文件'}`,
                                mediaUrl: ep.current_file?.access_url,
                                isVideo: true,
                              })
                            }
                            className="absolute top-4 right-2 px-2.5 py-1 rounded-md bg-black/60 hover:bg-black/80 text-white text-2xs font-medium backdrop-blur-xs transition-all opacity-0 group-hover/epvid:opacity-100 flex items-center gap-1 cursor-pointer"
                          >
                            <i className="ri-fullscreen-line"></i>
                            全屏放映
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-foreground-400 text-sm">
                  <i className="ri-film-line text-3xl block mb-2 opacity-40"></i>
                  尚无已上载的剧集素材
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Copyright & Attachments */}
          {activeTab === 'copyright' && (
            <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-6">
              <h3 className="text-base font-bold text-foreground-950 border-b border-gray-100 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-primary-500"></i>
                  版权证明与资质附件
                </div>
                {isNeedSupplement && (
                  <button
                    type="button"
                    onClick={() => setIsSupplementOpen(true)}
                    className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold transition-all inline-flex items-center gap-1"
                  >
                    <i className="ri-add-line"></i>
                    补充上传附件
                  </button>
                )}
              </h3>

              {/* Attachments List */}
              {application.attachments && application.attachments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {application.attachments.map((att, idx) => {
                    const isImg = isImageFile(att.upload_asset, att.display_name);
                    const isVid = isVideoFile(att.upload_asset, att.display_name);
                    const fileUrl = att.upload_asset?.access_url;

                    return (
                      <div key={idx} className="p-4 rounded-xl bg-gray-50 border border-gray-200/70 hover:border-primary-300 transition-all flex flex-col gap-3 group">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-foreground-900 truncate" title={att.display_name || '版权证明文件'}>
                              {att.display_name || '版权证明文件'}
                            </p>
                            <span className="text-2xs text-foreground-400 block mt-0.5">
                              {formatAttachmentCategory(att.category, att.is_supplement)}
                            </span>
                          </div>
                        </div>

                        {/* 内联媒体直接展示区域 */}
                        {fileUrl ? (
                          isImg ? (
                            <div
                              onClick={() =>
                                setPreviewMedia({
                                  open: true,
                                  title: att.display_name || '图片预览',
                                  mediaUrl: fileUrl,
                                  isVideo: false,
                                })
                              }
                              className="relative w-full h-36 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer group/img"
                            >
                              <img
                                src={fileUrl}
                                alt={att.display_name || '附件图片'}
                                className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white gap-1.5 text-xs font-semibold">
                                <i className="ri-zoom-in-line text-lg"></i>
                                点击大图预览
                              </div>
                            </div>
                          ) : isVid ? (
                            <div
                              onClick={() =>
                                setPreviewMedia({
                                  open: true,
                                  title: att.display_name || '视频预览',
                                  mediaUrl: fileUrl,
                                  isVideo: true,
                                })
                              }
                              className="relative w-full h-36 rounded-lg overflow-hidden bg-black border border-gray-200 cursor-pointer group/vid"
                            >
                              <video
                                src={fileUrl}
                                className="w-full h-full object-contain pointer-events-none"
                              />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white group-hover/vid:bg-black/50 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white text-xl group-hover/vid:scale-110 transition-transform shadow-lg">
                                  <i className="ri-play-fill ml-0.5"></i>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 rounded-lg bg-white border border-gray-200 flex items-center justify-between text-xs mt-auto">
                              <span className="text-foreground-500 truncate flex-1">普通文件 / 关联文档</span>
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-foreground-700 font-medium transition-all shrink-0 ml-2"
                              >
                                下载查看
                              </a>
                            </div>
                          )
                        ) : (
                          <div className="p-3 rounded-lg bg-gray-100 text-foreground-400 text-xs text-center mt-auto">
                            无在线资源访问地址
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-foreground-400 text-sm">
                  <i className="ri-folder-unknow-line text-3xl block mb-2 opacity-40"></i>
                  暂无附件列表数据
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 补充资料 Modal (POST /applications/{id}/supplement) */}
      {isSupplementOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-gray-100 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-add-circle-line text-orange-500"></i>
                补充提交资料 (/supplement)
              </h3>
              <button
                type="button"
                onClick={() => setIsSupplementOpen(false)}
                className="text-foreground-400 hover:text-foreground-700 text-xl cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleSupplementSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  资料分类 (Category)
                </label>
                <select
                  value={supplementCategory}
                  onChange={(e) => setSupplementCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary-500 bg-gray-50/50"
                >
                  <option value="COPYRIGHT_CHAIN">版权授权链 (COPYRIGHT_CHAIN)</option>
                  <option value="QUALIFICATION">资质能力 (QUALIFICATION)</option>
                  <option value="EPISODE">剧集素材 (EPISODE)</option>
                  <option value="OTHER">其他补充说明资料 (OTHER)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  资料文件显示名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="例如：补充剧本授权书证明.pdf"
                  value={supplementDisplayName}
                  onChange={(e) => setSupplementDisplayName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  上传资源 Asset ID (选填)
                </label>
                <input
                  type="text"
                  placeholder="可留空或填入已上传的资源 Asset ID"
                  value={supplementAssetId}
                  onChange={(e) => setSupplementAssetId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-primary-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsSupplementOpen(false)}
                  className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-foreground-700 text-xs sm:text-sm font-medium transition-all"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={supplementSubmitting}
                  className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-400 text-white text-xs sm:text-sm font-semibold transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {supplementSubmitting && <i className="ri-loader-4-line animate-spin"></i>}
                  <span>确认提交补充资料</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* 全屏媒体 (图片/视频) 预览 Modal */}
      <PortfolioPreviewModal
        open={previewMedia.open}
        title={previewMedia.title || '资源预览'}
        isVideo={previewMedia.isVideo}
        mediaUrl={previewMedia.mediaUrl}
        onClose={() => setPreviewMedia((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}
